import React, { useEffect, useMemo, useState } from 'react';
import {
  User as UserIcon,
  Calendar,
  Clock,
  Key,
  AlertCircle,
  CheckCircle,
  Loader,
  Edit,
  Phone,
  MapPin,
  BookOpen,
  ShieldCheck,
  Send,
  RefreshCw,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const IRAQI_PROVINCES = [
  'بغداد',
  'البصرة',
  'نينوى',
  'أربيل',
  'السليمانية',
  'دهوك',
  'كركوك',
  'الأنبار',
  'صلاح الدين',
  'ديالى',
  'واسط',
  'بابل',
  'كربلاء',
  'النجف',
  'القادسية',
  'المثنى',
  'ذي قار',
  'ميسان',
];

const EDUCATION_LEVELS = [
  'متوسط',
  'إعدادي',
  'دبلوم',
  'بكالوريوس',
  'ماجستير',
  'دكتوراه',
];

const PHONE_COUNTRY_CODE = '964';
const OTP_CODE_LENGTH = 6;

const buildFullPhoneNumber = (local: string): string => {
  const digits = local.replace(/\D/g, '');
  if (!digits) {
    return '';
  }

  const sanitized = digits.startsWith('0') ? digits.slice(1) : digits;
  if (!sanitized) {
    return '';
  }

  return `${PHONE_COUNTRY_CODE}${sanitized}`;
};

const extractLocalPhone = (full?: string | null): string => {
  if (!full) {
    return '';
  }

  let digits = full.replace(/\D/g, '');
  if (digits.startsWith(PHONE_COUNTRY_CODE)) {
    digits = digits.slice(PHONE_COUNTRY_CODE.length);
  }
  if (digits.startsWith('0')) {
    digits = digits.slice(1);
  }
  return digits;
};

const formatDisplayPhone = (full?: string | null): string => {
  if (!full) {
    return 'غير متوفر';
  }

  const digits = full.replace(/\D/g, '');
  if (!digits) {
    return 'غير متوفر';
  }

  const prefixed = digits.startsWith('+') ? digits : `+${digits}`;
  return prefixed.replace(/(\+\d{3})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4');
};

const formatCountdown = (seconds: number | null): string => {
  if (seconds === null) {
    return '';
  }
  const safeSeconds = Math.max(0, seconds);
  const m = Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (safeSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

type OtpStep = 'form' | 'verify';

type OtpMessage = {
  type: 'success' | 'error';
  text: string;
};

interface EditFormState {
  display_name: string;
  department: string;
  province: string;
  education_level: string;
  phone_number: string;
}

interface OtpFormState {
  full_name: string;
  province: string;
  education_level: string;
  phone_local: string;
}

export default function ProfilePage() {
  const {
    user,
    subscriptions,
    isLoading,
    error,
    fetchUserProfile,
    updateUserProfile,
    activateSerial,
    requestOtp,
    verifyOtp,
    clearOtpState,
    setOtpState,
    otpPhoneNumber,
    otpExpiresAt,
    clearError,
  } = useAuthStore();

  const [serialCode, setSerialCode] = useState('');
  const [isActivating, setIsActivating] = useState(false);
  const [activationMessage, setActivationMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>({
    display_name: '',
    department: '',
    province: '',
    education_level: '',
    phone_number: '',
  });

  const [otpForm, setOtpForm] = useState<OtpFormState>({
    full_name: '',
    province: '',
    education_level: '',
    phone_local: '',
  });
  const [otpStep, setOtpStep] = useState<OtpStep>(otpPhoneNumber ? 'verify' : 'form');
  const [otpCode, setOtpCode] = useState('');
  const [otpCountdown, setOtpCountdown] = useState<number | null>(null);
  const [otpMessage, setOtpMessage] = useState<OtpMessage | null>(null);
  const [lastOtpPayload, setLastOtpPayload] = useState<OtpFormState | null>(null);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    if (user) {
      setEditForm({
        display_name: user.display_name || '',
        department: user.department || '',
        province: user.province || '',
        education_level: user.education_level || '',
        phone_number: extractLocalPhone(user.phone_number),
      });
    }
  }, [user]);

  useEffect(() => {
    if (user?.is_login) {
      const localPhone = extractLocalPhone(user.phone_number);
      setOtpForm((prev) => ({
        ...prev,
        full_name: user.display_name || '',
        province: user.province || '',
        education_level: user.education_level || '',
        phone_local: localPhone,
      }));
      setOtpStep('form');
      setOtpCode('');
      setOtpMessage(null);
    }
  }, [user?.is_login, user?.display_name, user?.province, user?.education_level, user?.phone_number]);

  useEffect(() => {
    if (otpPhoneNumber) {
      setOtpStep('verify');
      setOtpForm((prev) => ({
        ...prev,
        phone_local: extractLocalPhone(otpPhoneNumber),
      }));
    } else {
      setOtpStep('form');
    }
  }, [otpPhoneNumber]);

  useEffect(() => {
    if (!otpExpiresAt) {
      setOtpCountdown(null);
      return;
    }

    const expiry = new Date(otpExpiresAt).getTime();
    if (Number.isNaN(expiry)) {
      setOtpCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const diff = Math.ceil((expiry - Date.now()) / 1000);
      setOtpCountdown(diff > 0 ? diff : 0);
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [otpExpiresAt]);

  const otpExpired = useMemo(() => otpCountdown !== null && otpCountdown <= 0, [otpCountdown]);
  const normalizedOtpPhone = useMemo(() => buildFullPhoneNumber(otpForm.phone_local), [otpForm.phone_local]);
  const maskedOtpPhone = useMemo(() => formatDisplayPhone(otpPhoneNumber || normalizedOtpPhone), [otpPhoneNumber, normalizedOtpPhone]);

  const handleActivateSerial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serialCode.trim()) return;

    setIsActivating(true);
    setActivationMessage('');

    const success = await activateSerial(serialCode.trim());

    if (success) {
      setActivationMessage('تم تفعيل السيريال نمبر بنجاح!');
      setSerialCode('');
    } else {
      setActivationMessage('فشل في تفعيل السيريال نمبر. تأكد من صحة الرمز.');
    }

    setIsActivating(false);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    const fullPhone = buildFullPhoneNumber(editForm.phone_number);
    const payload = {
      display_name: editForm.display_name.trim() || null,
      department: editForm.department.trim() || null,
      province: editForm.province || null,
      education_level: editForm.education_level || null,
      phone_number: fullPhone || null,
    };

    await updateUserProfile(payload);
    setIsEditing(false);
  };

  const handleOtpFormChange = (field: keyof OtpFormState, value: string) => {
    if (field === 'phone_local') {
      const digits = value.replace(/\D/g, '').slice(0, 11);
      setOtpForm((prev) => ({ ...prev, phone_local: digits }));
      return;
    }

    setOtpForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateOtpForm = (): { valid: boolean; message?: string } => {
    if (!otpForm.full_name.trim()) {
      return { valid: false, message: 'الرجاء إدخال الاسم الثلاثي.' };
    }

    if (!otpForm.province) {
      return { valid: false, message: 'الرجاء اختيار المحافظة.' };
    }

    if (!otpForm.education_level) {
      return { valid: false, message: 'الرجاء اختيار التحصيل الدراسي.' };
    }

    const phone = buildFullPhoneNumber(otpForm.phone_local);
    if (!phone || phone.length < PHONE_COUNTRY_CODE.length + 8) {
      return { valid: false, message: 'الرجاء إدخال رقم هاتف عراقي صالح.' };
    }

    return { valid: true };
  };

  const buildOtpPayload = () => ({
    full_name: otpForm.full_name.trim(),
    province: otpForm.province,
    education_level: otpForm.education_level,
    phone_number: buildFullPhoneNumber(otpForm.phone_local),
  });

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpMessage(null);
    clearError();

    const validation = validateOtpForm();
    if (!validation.valid) {
      setOtpMessage({ type: 'error', text: validation.message || 'الرجاء إكمال الحقول المطلوبة.' });
      return;
    }

    const payload = buildOtpPayload();
    const success = await requestOtp(payload);

    if (success) {
      setOtpMessage({ type: 'success', text: 'تم إرسال رمز التحقق برسالة نصية إلى رقم هاتفك.' });
      setOtpCode('');
      setOtpStep('verify');
      setLastOtpPayload(otpForm);
    } else {
      setOtpMessage({ type: 'error', text: 'تعذر إرسال رمز التحقق. حاول مرة أخرى.' });
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpMessage(null);
    clearError();

    if (!normalizedOtpPhone) {
      setOtpMessage({ type: 'error', text: 'رقم الهاتف غير صالح. الرجاء العودة وتعبئة البيانات من جديد.' });
      setOtpStep('form');
      return;
    }

    if (otpCode.length !== OTP_CODE_LENGTH) {
      setOtpMessage({ type: 'error', text: 'الرجاء إدخال رمز مكون من 6 أرقام.' });
      return;
    }

    const success = await verifyOtp(normalizedOtpPhone, otpCode);

    if (success) {
      setOtpMessage(null);
      setOtpCode('');
      setLastOtpPayload(null);
    } else {
      setOtpMessage({ type: 'error', text: 'رمز التحقق غير صحيح. حاول مرة أخرى.' });
    }
  };

  const handleResendOtp = async () => {
    setOtpMessage(null);
    clearError();

    const payload = lastOtpPayload
      ? {
          full_name: lastOtpPayload.full_name.trim(),
          province: lastOtpPayload.province,
          education_level: lastOtpPayload.education_level,
          phone_number: buildFullPhoneNumber(lastOtpPayload.phone_local),
        }
      : buildOtpPayload();

    if (!payload.full_name || !payload.province || !payload.education_level || !payload.phone_number) {
      setOtpMessage({
        type: 'error',
        text: 'الرجاء العودة لتعبئة البيانات قبل طلب رمز جديد.',
      });
      setOtpStep('form');
      return;
    }

    const success = await requestOtp(payload);
    if (success) {
      setOtpMessage({ type: 'success', text: 'تم إرسال رمز تحقق جديد إلى رقم هاتفك.' });
      setOtpCode('');
      setLastOtpPayload({ ...otpForm });
    } else {
      setOtpMessage({ type: 'error', text: 'تعذر إرسال رمز جديد حالياً. حاول لاحقاً.' });
    }
  };

  const handleResetOtpFlow = () => {
    clearOtpState();
    setOtpMessage(null);
    setOtpCode('');
    setOtpStep('form');
  };

  const otpCodeReady = otpCode.length === OTP_CODE_LENGTH && !otpExpired;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (isValid: boolean) => {
    return isValid ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50';
  };

  if (isLoading && !user) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-stone-600">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2">الملف الشخصي</h1>
          <p className="text-stone-600">إدارة معلوماتك الشخصية واشتراكاتك</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
              <button onClick={clearError} className="mr-auto text-red-600 hover:text-red-700">
                ×
              </button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-stone-900">معلومات المستخدم</h2>
              {user?.is_login && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="p-2 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <Edit className="w-5 h-5" />
                </button>
              )}
            </div>

            {user?.is_login && isEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">الاسم الثلاثي</label>
                  <input
                    type="text"
                    value={editForm.display_name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, display_name: e.target.value }))}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="أدخل اسمك الثلاثي"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">القسم</label>
                  <input
                    type="text"
                    value={editForm.department}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, department: e.target.value }))}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="أدخل قسمك"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">المحافظة</label>
                  <select
                    value={editForm.province}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, province: e.target.value }))}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="">اختر المحافظة</option>
                    {IRAQI_PROVINCES.map((province) => (
                      <option key={province} value={province}>
                        {province}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">التحصيل الدراسي</label>
                  <select
                    value={editForm.education_level}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, education_level: e.target.value }))}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="">اختر المستوى الدراسي</option>
                    {EDUCATION_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">رقم الهاتف</label>
                  <div className="flex items-center border border-stone-300 rounded-lg overflow-hidden">
                    <span className="px-3 py-3 bg-stone-100 text-stone-600 text-sm">+{PHONE_COUNTRY_CODE}</span>
                    <input
                      type="tel"
                      value={editForm.phone_number}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, phone_number: e.target.value.replace(/\D/g, '').slice(0, 11) }))}
                      className="flex-1 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="مثال: 7701234567"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 rtl:space-x-reverse">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="flex-1 bg-stone-200 text-stone-700 py-3 rounded-lg font-semibold hover:bg-stone-300 transition-colors"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <UserIcon className="w-5 h-5 text-stone-500" />
                  <div>
                    <p className="text-sm text-stone-500">الاسم الثلاثي</p>
                    <p className="font-semibold text-stone-900">{user?.display_name || 'لم يتم تحديده'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <BookOpen className="w-5 h-5 text-stone-500" />
                  <div>
                    <p className="text-sm text-stone-500">التحصيل الدراسي</p>
                    <p className="font-semibold text-stone-900">{user?.education_level || 'لم يتم تحديده'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <MapPin className="w-5 h-5 text-stone-500" />
                  <div>
                    <p className="text-sm text-stone-500">المحافظة</p>
                    <p className="font-semibold text-stone-900">{user?.province || 'لم يتم تحديدها'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <Phone className="w-5 h-5 text-stone-500" />
                  <div>
                    <p className="text-sm text-stone-500">رقم الهاتف</p>
                    <p className="font-semibold text-stone-900">{formatDisplayPhone(user?.phone_number)}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <CheckCircle className="w-5 h-5 text-stone-500" />
                  <div>
                    <p className="text-sm text-stone-500">حالة تسجيل الدخول</p>
                    <p className={`font-semibold ${user?.is_login ? 'text-green-600' : 'text-stone-500'}`}>
                      {user?.is_login ? 'مسجل الدخول' : 'مستخدم ضيف'}
                    </p>
                  </div>
                </div>

                {!user?.is_login && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-stone-900 mb-2 flex items-center space-x-2 rtl:space-x-reverse">
                      <ShieldCheck className="w-5 h-5" />
                      <span>تسجيل الدخول عبر رمز SMS</span>
                    </h3>
                    <p className="text-sm text-stone-600 mb-4">
                      أدخل بياناتك لإرسال رمز تحقق عبر SMS. عند إدخال الرمز بشكل صحيح سيتم حفظ معلوماتك وربط اشتراكاتك برقم الهاتف.
                    </p>

                    {otpMessage && (
                      <div
                        className={`mb-4 rounded-lg border p-3 text-sm ${
                          otpMessage.type === 'success'
                            ? 'border-green-200 bg-green-50 text-green-700'
                            : 'border-red-200 bg-red-50 text-red-700'
                        }`}
                      >
                        {otpMessage.text}
                      </div>
                    )}

                    {otpStep === 'form' ? (
                      <form onSubmit={handleRequestOtp} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">الاسم الثلاثي</label>
                          <input
                            type="text"
                            value={otpForm.full_name}
                            onChange={(e) => handleOtpFormChange('full_name', e.target.value)}
                            className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="أدخل اسمك الثلاثي"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">المحافظة</label>
                          <select
                            value={otpForm.province}
                            onChange={(e) => handleOtpFormChange('province', e.target.value)}
                            className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          >
                            <option value="">اختر المحافظة</option>
                            {IRAQI_PROVINCES.map((province) => (
                              <option key={province} value={province}>
                                {province}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">التحصيل الدراسي</label>
                          <select
                            value={otpForm.education_level}
                            onChange={(e) => handleOtpFormChange('education_level', e.target.value)}
                            className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          >
                            <option value="">اختر المستوى الدراسي</option>
                            {EDUCATION_LEVELS.map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">رقم الهاتف (مع مفتاح العراق)</label>
                          <div className="flex items-center border border-stone-300 rounded-lg overflow-hidden">
                            <span className="px-3 py-3 bg-stone-100 text-stone-600 text-sm">+{PHONE_COUNTRY_CODE}</span>
                            <input
                              type="tel"
                              value={otpForm.phone_local}
                              onChange={(e) => handleOtpFormChange('phone_local', e.target.value)}
                              className="flex-1 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                              placeholder="مثال: 7701234567"
                            />
                          </div>
                        </div>

                        <button
                          type="submit"
                          className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 rtl:space-x-reverse"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Loader className="w-5 h-5 animate-spin" />
                              <span>جاري الإرسال...</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5" />
                              <span>إرسال رمز التحقق</span>
                            </>
                          )}
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleVerifyOtp} className="space-y-4">
                        <div className="bg-stone-100 border border-stone-200 rounded-lg p-4 text-sm text-stone-700">
                          <p className="font-semibold mb-1">أرسلنا رمز التحقق إلى:</p>
                          <p className="flex items-center space-x-2 rtl:space-x-reverse">
                            <Phone className="w-4 h-4" />
                            <span>{maskedOtpPhone}</span>
                          </p>
                          {otpCountdown !== null && (
                            <p className="flex items-center space-x-2 rtl:space-x-reverse mt-2">
                              <Clock className="w-4 h-4" />
                              <span>
                                {otpExpired
                                  ? 'انتهت صلاحية الرمز. يمكنك طلب رمز جديد.'
                                  : `الرمز صالح لمدة ${formatCountdown(otpCountdown)}`}
                              </span>
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">رمز التحقق</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={OTP_CODE_LENGTH}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, OTP_CODE_LENGTH))}
                            className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent tracking-widest text-center text-lg"
                            placeholder="أدخل الرمز المكون من 6 أرقام"
                          />
                        </div>

                        <button
                          type="submit"
                          className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 rtl:space-x-reverse"
                          disabled={isLoading || !otpCodeReady}
                        >
                          {isLoading ? (
                            <>
                              <Loader className="w-5 h-5 animate-spin" />
                              <span>جاري التحقق...</span>
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="w-5 h-5" />
                              <span>تأكيد الرمز وتسجيل الدخول</span>
                            </>
                          )}
                        </button>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-stone-600">
                          <button
                            type="button"
                            onClick={handleResendOtp}
                            className="flex items-center space-x-1 rtl:space-x-reverse text-amber-600 hover:text-amber-700"
                            disabled={isLoading}
                          >
                            <RefreshCw className="w-4 h-4" />
                            <span>إرسال رمز جديد</span>
                          </button>
                          <button
                            type="button"
                            onClick={handleResetOtpFlow}
                            className="text-stone-500 hover:text-stone-700"
                            disabled={isLoading}
                          >
                            تعديل البيانات أو استخدام رقم آخر
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">تفعيل اشتراك جديد</h2>

            <form onSubmit={handleActivateSerial} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">السيريال نمبر</label>
                <div className="relative">
                  <Key className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input
                    type="text"
                    value={serialCode}
                    onChange={(e) => setSerialCode(e.target.value.toUpperCase())}
                    placeholder="XXXX-XXXX-XXXX"
                    className="w-full pl-4 pr-12 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent font-mono"
                    disabled={isActivating}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={!serialCode.trim() || isActivating}
                className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                {isActivating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>جاري التفعيل...</span>
                  </>
                ) : (
                  <>
                    <Key className="w-5 h-5" />
                    <span>تفعيل الاشتراك</span>
                  </>
                )}
              </button>
            </form>

            {activationMessage && (
              <div
                className={`mt-4 p-3 rounded-lg ${
                  activationMessage.includes('بنجاح')
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}
              >
                <p className="text-sm">{activationMessage}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6">اشتراكاتي الحالية</h2>

          {subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 text-lg">لا توجد اشتراكات نشطة</p>
              <p className="text-stone-400 text-sm mt-2">استخدم السيريال نمبر لتفعيل اشتراك جديد</p>
            </div>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((subscription, index) => (
                <div
                  key={index}
                  className="border border-stone-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="mb-4 sm:mb-0">
                      <h3 className="text-lg font-semibold text-stone-900 mb-2">
                        {subscription.course_title}
                      </h3>
                      <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-stone-600">
                        <div className="flex items-center space-x-1 rtl:space-x-reverse">
                          <Calendar className="w-4 h-4" />
                          <span>من {formatDate(subscription.start_date)}</span>
                        </div>
                        <div className="flex items-center space-x-1 rtl:space-x-reverse">
                          <Calendar className="w-4 h-4" />
                          <span>إلى {formatDate(subscription.end_date)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="text-left rtl:text-right">
                      <div
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          getStatusColor(subscription.is_valid)
                        }`}
                      >
                        {subscription.is_valid ? 'نشط' : 'منتهي'}
                      </div>
                      {subscription.is_valid && (
                        <div className="flex items-center space-x-1 rtl:space-x-reverse mt-2 text-sm text-stone-600">
                          <Clock className="w-4 h-4" />
                          <span>{subscription.days_remaining} يوم متبقي</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
